import { useState } from "react"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { formatMoney } from "@/lib/formatters"
import type { StatusTone } from "@/lib/status-colors"
import { TillFigure } from "@/features/staff/till-figure"
import { ReportFigures, ReportList, ReportRow } from "@/features/staff/reports/report-block"
import { formatShare, tStats } from "@/features/staff/stats/format"
import type { AbcClass, StatsMix, StatsMixItem } from "@/features/staff/stats/types"

/**
 * Which dishes carry the menu, over the window.
 *
 * **Its money is not a headline and never will be.** This counts dishes; the
 * KPI band counts payments. A split share carries a subtotal that belongs to no
 * dish, and tips belong to no dish at all, so the two cannot be made to agree
 * without lying about one of them. The card says so rather than leaving the
 * reader to discover it.
 *
 * The ABC classes are tones, not verdicts: a C is the tail of the menu, not a
 * failure, and colouring it `destructive` would tell an owner to delete dishes
 * that pay the rent on a slow Tuesday. Same mapping as `/reports`, deliberately.
 */
const ABC_TONE: Record<AbcClass, StatusTone> = {
  a: "available",
  b: "reserved",
  c: "disabled",
}

const HEADLINE_CLASSES: AbcClass[] = ["a", "b"]

/** A dish with no recipe gets a sentence, not a dash. No number is invented. */
function marginNote(item: StatsMixItem): string {
  return item.margin === null
    ? tStats("mix.item_margin_unknown")
    : tStats("mix.item_margin", { margin: formatMoney(item.margin) })
}

export function StatsMixBlock({ mix }: { mix: StatsMix }) {
  const [showAll, setShowAll] = useState(false)

  if (mix.itemCount === 0) return null

  const costed = mix.costedQuantity > 0

  const classCount = (abc: AbcClass) => mix.items.filter((item) => item.abcClass === abc).length
  const classShare = (abc: AbcClass) =>
    mix.items.filter((item) => item.abcClass === abc).reduce((sum, item) => sum + item.share, 0)

  const headline = mix.items.filter((item) => HEADLINE_CLASSES.includes(item.abcClass))
  const visible = showAll || headline.length === 0 ? mix.items : headline
  const hidden = mix.items.length - visible.length

  return (
    <Card density="compact" data-testid="stats-mix">
      <CardHeader className="border-b">
        <CardTitle>{tStats("mix.title")}</CardTitle>
        <CardDescription>{tStats("notes.mix_basis")}</CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        <ReportFigures>
          {(["a", "b", "c"] as AbcClass[]).map((abc) => (
            <TillFigure
              key={abc}
              label={tStats(`mix.class_${abc}`)}
              value={tStats("mix.class_count", { count: classCount(abc) })}
              hint={tStats("mix.share_of_value", { share: formatShare(classShare(abc)) })}
              testId={`stats-mix-class-${abc}`}
            />
          ))}
        </ReportFigures>

        <ReportList testId="stats-mix-items">
          {visible.map((item) => (
            <ReportRow
              key={item.locationItemId ?? item.name}
              testId={`stats-mix-item-${item.locationItemId ?? "unknown"}`}
              tone={ABC_TONE[item.abcClass]}
              label={item.name}
              sublabel={[
                tStats("mix.item_meta", {
                  abc: tStats(`mix.class_${item.abcClass}`),
                  share: formatShare(item.share),
                }),
                costed ? marginNote(item) : null,
              ]
                .filter(Boolean)
                .join(" · ")}
              meta={tStats("mix.quantity", { count: item.quantity })}
              amount={formatMoney(item.net)}
            />
          ))}
        </ReportList>

        {hidden > 0 ? (
          <Button
            variant="secondary"
            className="w-full"
            data-testid="stats-mix-show-all"
            onClick={() => setShowAll(true)}
          >
            {tStats("mix.show_all", { count: hidden })}
          </Button>
        ) : null}

        {/* How much of the mix the margin is built on. A window that is a third
            costed says so instead of implying it speaks for the whole menu. */}
        {costed ? (
          <p className="text-xs text-muted-foreground" data-testid="stats-mix-cost-coverage">
            {tStats("notes.cost_coverage", { costed: mix.costedQuantity, total: mix.quantity })}
          </p>
        ) : null}
      </CardContent>
    </Card>
  )
}
